import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { KF_AUTH_CONFIG } from '../auth/kf-auth.config';
import { KF_APP_CONFIG } from './kf-app.config';
import { LoginRequest, LoginResponse } from './tenant.models';

/**
 * The two calls every vertical makes to the KanzenFlow Admin API: exchange the
 * Entra sign-in for the user's tenant list, and remember a primary tenant.
 *
 * The admin API is one deployment shared by every environment and every
 * vertical, which is why its URL is app configuration and not derived from the
 * app's own API URL.
 */
@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private readonly http = inject(HttpClient);
  private readonly msal = inject(MsalService);
  private readonly auth = inject(KF_AUTH_CONFIG);
  private readonly app = inject(KF_APP_CONFIG);

  private url(path: string): string {
    return `${this.app.adminApiUrl.replace(/\/+$/, '')}/${path}`;
  }

  /**
   * Signs the active Entra account in to the admin API and returns the user
   * and the tenants they may open. Acquires a fresh token first: MSAL v5 may
   * not have populated its cache when the redirect completes, and a stale
   * token here is a confusing 401 on the very first screen.
   */
  login(): Observable<LoginResponse> {
    const account = this.msal.instance.getActiveAccount();
    if (!account) {
      return throwError(() => new Error('No signed-in account.'));
    }

    const request = { scopes: [this.auth.apiScope], account };
    return from(this.msal.instance.acquireTokenSilent(request)).pipe(
      // A silent renewal can need the user (consent, password change, MFA
      // policy). Hand over to the redirect flow rather than surfacing a raw
      // MSAL error on the tenant picker.
      catchError((error) => {
        if (error instanceof InteractionRequiredAuthError) {
          return from(this.msal.instance.acquireTokenRedirect(request)).pipe(
            switchMap(() => throwError(() => error))
          );
        }
        return throwError(() => error);
      }),
      switchMap((token) => {
        const request: LoginRequest = {
          AzureAdToken: token.accessToken,
          Email: account.username,
          ADObjectId: account.localAccountId,
        };
        return this.http.post<LoginResponse>(this.url('auth/login'), request);
      })
    );
  }

  /** null clears the primary, so the picker is shown every time. */
  setPrimaryTenant(userId: number, tenantCompanyId: number | null): Observable<string> {
    return this.http.post<string>(this.url('auth/setprimarytenant'), {
      UserId: userId,
      TenantCompanyId: tenantCompanyId,
    });
  }
}
